import { useEffect, useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useCars, useCreateAssignment, useReturnAssignment } from '../../hooks/useApi';
import { errorMessage } from '../../services/api';
import { toast } from '../../store';
import type { Assignment } from '../../types';
import { carLabel, driverName, formatDateTime, formatNumber, localInputToIso, toDateTimeInput } from '../../utils/helpers';
import { Button } from '../ui/Button';
import { Input, Textarea } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { CarSelect, DriverSelect } from './EntitySelects';
import { t } from '../../i18n';

interface CreateValues {
  car_id: string;
  driver_id: string;
  assigned_at: string;
  start_km: string;
  purpose: string;
  notes: string;
}

interface CreateProps {
  isOpen: boolean;
  onClose: () => void;
  carId?: number;
  driverId?: number;
}

export function AssignmentCreateModal({ isOpen, onClose, carId, driverId }: CreateProps) {
  const create = useCreateAssignment();
  const { data: carsData } = useCars({ page_size: 500, status: 'active' }, isOpen);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateValues>();

  useLayoutEffect(() => {
    if (isOpen) {
      reset({
        car_id: carId ? String(carId) : '',
        driver_id: driverId ? String(driverId) : '',
        assigned_at: toDateTimeInput(),
        start_km: '',
        purpose: '',
        notes: '',
      });
    }
  }, [isOpen, carId, driverId, reset]);

  const selectedCar = carsData?.cars.find((c) => String(c.id) === (carId ? String(carId) : watch('car_id')));

  useEffect(() => {
    if (selectedCar) setValue('start_km', String(selectedCar.current_km));
  }, [selectedCar, setValue]);

  const onSubmit = async (values: CreateValues) => {
    try {
      await create.mutateAsync({
        // Disabled (pre-selected) fields are not included in the submitted values
        car_id: carId ?? Number(values.car_id),
        driver_id: driverId ?? Number(values.driver_id),
        assigned_at: localInputToIso(values.assigned_at),
        start_km: values.start_km === '' ? null : Number(values.start_km),
        purpose: values.purpose || null,
        notes: values.notes || null,
      });
      toast.success(t('Vozidlo bolo pridelené'));
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, t('Pridelenie sa nepodarilo vytvoriť')));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      busy={create.isPending}
      size="lg"
      title={t('Prideliť vozidlo')}
      description={t('Prideliť je možné iba aktívne vozidlo, ktoré práve nikto nemá.')}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={create.isPending}>
            {t('Zrušiť')}
          </Button>
          <Button type="submit" form="assignment-form" loading={create.isPending}>
            {t('Prideliť')}
          </Button>
        </>
      }
    >
      <form id="assignment-form" onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-4 sm:grid-cols-2" noValidate>
        <CarSelect
          label={t('Vozidlo')}
          required
          onlyActive
          disabled={!!carId}
          error={errors.car_id?.message}
          {...register('car_id', { required: carId ? false : t('Vyberte vozidlo') })}
        />
        <DriverSelect
          label={t('Vodič')}
          required
          disabled={!!driverId}
          error={errors.driver_id?.message}
          {...register('driver_id', { required: driverId ? false : t('Vyberte vodiča') })}
        />
        <Input label={t('Dátum a čas prevzatia')} type="datetime-local" required {...register('assigned_at', { required: true })} />
        <Input
          label={t('Počiatočný stav km')}
          type="number"
          min={0}
          hint={selectedCar ? t('Aktuálne v evidencii: {currentKm} km', { currentKm: formatNumber(selectedCar.current_km) }) : undefined}
          {...register('start_km')}
        />
        <Input label={t('Účel')} placeholder={t('napr. služobné auto, cesta do Košíc')} wrapperClassName="sm:col-span-2" {...register('purpose')} />
        <Textarea label={t('Poznámky')} wrapperClassName="sm:col-span-2" {...register('notes')} />
      </form>
    </Modal>
  );
}

interface ReturnValues {
  returned_at: string;
  end_km: string;
  notes: string;
}

export function AssignmentReturnModal({ assignment, onClose }: { assignment: Assignment | null; onClose: () => void }) {
  const returnCar = useReturnAssignment();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ReturnValues>();

  useLayoutEffect(() => {
    if (assignment) {
      reset({ returned_at: toDateTimeInput(), end_km: String(assignment.car?.current_km ?? assignment.start_km ?? ''), notes: '' });
    }
  }, [assignment, reset]);

  const onSubmit = async (values: ReturnValues) => {
    if (!assignment) return;
    try {
      await returnCar.mutateAsync({
        id: assignment.id,
        data: {
          returned_at: localInputToIso(values.returned_at),
          end_km: values.end_km === '' ? null : Number(values.end_km),
          notes: values.notes || null,
        },
      });
      toast.success(t('Pridelenie bolo ukončené'));
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, t('Pridelenie sa nepodarilo ukončiť')));
    }
  };

  const startKm = assignment?.start_km ?? 0;

  return (
    <Modal
      isOpen={!!assignment}
      onClose={onClose}
      busy={returnCar.isPending}
      title={t('Vrátenie vozidla')}
      description={
        assignment && (
          <>
            {carLabel(assignment.car)} · {driverName(assignment.driver)} · od {formatDateTime(assignment.assigned_at)}
          </>
        )
      }
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={returnCar.isPending}>
            {t('Zrušiť')}
          </Button>
          <Button type="submit" form="return-form" loading={returnCar.isPending}>
            {t('Ukončiť pridelenie')}
          </Button>
        </>
      }
    >
      <form id="return-form" onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-4 sm:grid-cols-2" noValidate>
        <Input label={t('Dátum a čas vrátenia')} type="datetime-local" required {...register('returned_at', { required: true })} />
        <Input
          label={t('Konečný stav km')}
          type="number"
          min={startKm}
          hint={t('Počiatočný stav: {startKm} km', { startKm: formatNumber(startKm) })}
          error={errors.end_km?.message}
          {...register('end_km', {
            validate: (v) => v === '' || Number(v) >= startKm || t('Nesmie byť nižší ako počiatočný stav'),
          })}
        />
        <Textarea label={t('Poznámka k vráteniu')} placeholder={t('napr. poškodenie, stav paliva')} wrapperClassName="sm:col-span-2" {...register('notes')} />
      </form>
    </Modal>
  );
}
